import React, { useCallback, useEffect, useState } from "react";
import { Lock, Rocket } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useRelease } from "../lib/release";
import { ReleaseChecklist } from "../components/launch/ReleaseChecklist";
import { ReleaseNotesCard } from "../components/launch/ReleaseNotesCard";
import { LinkStatus } from "../components/launch/LinkStatus";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

/** Per-environment on/off switches for pages and nominated in-page features. */
export default function AdminRelease() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  const [runsKey, setRunsKey] = useState(0);  const { refresh: refreshFlags } = useRelease();

  const load = useCallback(async () => {
    const { data: payload } = await api.get("/admin/release");
    setData(payload);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const flip = async () => {
    const row = pending;
    const next = !row.enabled;
    setSaving(row.key);
    try {
      await api.put(`/admin/release/${row.key}`, { enabled: next, note: note.trim() });
      toast.success(`${row.label} switched ${next ? "on" : "off"}`);
      setPending(null);
      setNote("");
      await load();
      await refreshFlags();
      setRunsKey((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  const ask = (row) => { setPending(row); setNote(""); };

  if (!data) return <div className="p-10 eyebrow">Loading…</div>;

  const groups = data.group_order.filter((group) => data.rows.some((row) => row.group === group));

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:py-12 lg:px-10" data-testid="admin-release-page">
      <div className="eyebrow mb-2">Release control · {data.environment} ·{" "}
        <span className="font-mono normal-case tracking-normal" data-testid="release-build-version">v{data.build.version}{data.build.built_at ? ` built ${when(data.build.built_at)}` : ""}</span>
      </div>
      <h1 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-3" style={{ color: "var(--ink)" }}>
        What is live
      </h1>
      <p className="text-sm max-w-3xl mb-8" style={{ color: "var(--muted)" }}>
        Publishing ships the whole portal; these switches decide what is reachable in{" "}
        <span className="font-semibold">{data.environment}</span>. A page switched off disappears
        from the navigation, its link redirects to the dashboard and its data requests are refused —
        for everyone, admins included. Flipping a switch is instant and needs no publish. The{" "}
        {data.other_environment} column comes from the linked site and can only be changed from inside
        that site.
      </p>

      <LinkStatus data={data} />

      <div className="grid grid-cols-2 border md:grid-cols-4 mb-6" style={{ borderColor: "var(--border)" }} data-testid="release-summary">
        <Cell label="Pages live" value={`${data.summary.pages_live}/${data.summary.pages_total}`} testId="release-pages-live"/>
        <Cell label="Features live" value={`${data.summary.features_live}/${data.summary.features_total}`} testId="release-features-live"/>
        <Cell label={`Off in ${data.environment}`} value={data.summary.off_here.length} testId="release-off-here"/>
        <Cell label={`Waiting for ${data.other_environment}`} value={data.summary.pending_promotion.length} testId="release-pending-count"/>
      </div>

      {Boolean(data.summary.pending_promotion.length) && (
        <div className="border p-5 mb-10" style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }} data-testid="release-pending-list">
          <div className="eyebrow mb-2 flex items-center gap-2"><Rocket size={13}/> Queued for promotion</div>
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            On here, still off in {data.other_environment}: {data.summary.pending_promotion.join(", ")}.
          </p>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-2 mb-10">
        <ReleaseChecklist suggestedVersion={data.link.newer_on_peer ? data.link.peer_version : ""} refreshKey={runsKey} />
        <ReleaseNotesCard onSaved={load} />
      </div>

      <Dialog open={Boolean(pending)} onOpenChange={(open) => { if (!open) { setPending(null); setNote(""); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="release-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Switch {pending?.label} {pending?.enabled ? "off" : "on"}
            </DialogTitle>
            <DialogDescription className="text-sm">
              {pending?.enabled
                ? `In ${data.environment}: ${pending?.description}`
                : `This makes ${pending?.label} reachable in ${data.environment} for the roles already entitled to it.`}
            </DialogDescription>
          </DialogHeader>
          <label className="field-label">What changed? (one sentence, kept in the audit trail)</label>
          <textarea
            rows={3}
            className="w-full border px-3 py-2 text-sm outline-none"
            style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Vendor filter added to the invoice queue; ready for clients"
            data-testid="release-note-input"
          />
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <button type="button" className="btn-ghost"
                    onClick={() => { setPending(null); setNote(""); }} data-testid="release-note-cancel">
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={flip}
                    disabled={!note.trim() || saving === pending?.key}
                    data-testid="release-note-confirm">
              {saving === pending?.key ? "Saving…" : `Switch ${pending?.enabled ? "off" : "on"}`}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {groups.map((group) => (
        <section key={group} className="mb-10">
          <h2 className="font-heading font-bold text-2xl mb-4" style={{ color: "var(--ink)" }}>{group}</h2>
          <div className="border max-h-[460px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={`release-table-region-${group.toLowerCase()}`}>
            <table className="w-full text-sm min-w-[820px]" data-testid={`release-table-${group.toLowerCase()}`}>
              <thead>
                <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
                  <th className="px-4 py-3">Page or feature</th>
                  <th className="px-4 py-3">What turning it off hides</th>
                  <th className="px-4 py-3">{data.other_environment}</th>
                  <th className="px-4 py-3">Last changed</th>
                  <th className="px-4 py-3 text-right">{data.environment}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.filter((row) => row.group === group).map((row) => (
                  <tr key={row.key} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`release-row-${row.key}`}>
                    <td className="px-4 py-3 font-medium" style={{ color: "var(--ink)" }}>
                      <span className="flex items-center gap-2">
                        {row.label}
                        {row.kind === "feature" && <span className="pill text-[9px]">Feature</span>}
                        {row.protected && (
                          <span className="pill text-[9px] flex items-center gap-1" data-testid={`release-protected-${row.key}`}>
                            <Lock size={10}/> Always on
                          </span>
                        )}
                      </span>
                      <span className="block font-mono text-[10px] mt-0.5" style={{ color: "var(--muted)" }}>{row.route}</span>
                    </td>
                    <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{row.description}</td>
                    <td className="px-4 py-3" style={{ color: "var(--muted)" }} data-testid={`release-other-${row.key}`}>
                      {row.other_environment.known ? (row.other_environment.enabled ? "On" : "Off") : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--muted)" }}>
                      {row.changed_by ? `${row.changed_by} · ${when(row.changed_at)}` : "Never changed"}
                      {row.note && (
                        <span className="block mt-1 italic" data-testid={`release-note-${row.key}`}>
                          “{row.note}”
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => ask(row)}
                        disabled={row.protected || saving === row.key}
                        className="inline-flex h-6 w-11 items-center p-0.5 transition-colors duration-200"
                        style={{
                          background: row.enabled ? "var(--ink)" : "transparent",
                          border: "1px solid var(--border-strong)",
                          cursor: row.protected ? "not-allowed" : "pointer",
                          opacity: row.protected ? 0.45 : 1,
                        }}
                        aria-label={`${row.enabled ? "Switch off" : "Switch on"} ${row.label}`}
                        data-testid={`release-toggle-${row.key}`}
                      >
                        <span
                          className="block h-4 w-4 transition-transform duration-200"
                          style={{
                            background: row.enabled ? "var(--surface)" : "var(--border-strong)",
                            transform: row.enabled ? "translateX(20px)" : "translateX(0)",
                          }}
                        />
                      </button>
                      <span className="block text-[10px] mt-1" style={{ color: "var(--muted)" }} data-testid={`release-state-${row.key}`}>
                        {row.enabled ? "On" : "Off"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

function Cell({ label, value, testId }) {
  return (
    <div className="border-r px-4 py-3 last:border-r-0" style={{ borderColor: "var(--border)" }} data-testid={testId}>
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="mt-1 font-heading text-lg font-bold" style={{ color: "var(--ink)" }}>{value}</div>
    </div>
  );
}
